import { toast } from "react-toastify";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import {
  deleteDisciplinaryFailure,
  deleteDisciplinarySuccess,
  fetchDisciplinary,
  fetchDisciplinaryFailure,
  fetchLocationDisciplinaryFailure,
  fetchLocationDisciplinarySuccess,
  fetchDisciplinaryRecord,
  fetchDisciplinaryRecordFailure,
  fetchDisciplinaryRecordSuccess,
  fetchDisciplinarySuccess,
  postDisciplinaryActionFailure,
  postDisciplinaryActionSuccess,
  saveDisciplinaryFailure,
  saveDisciplinarySuccess,
  uploadDisciplinaryAttachmentFailure,
  uploadDisciplinaryAttachmentSuccess,
} from "./actions";
import {
  DELETE_DISCIPLINARY,
  FETCH_DISCIPLINARY,
  FETCH_DISCIPLINARY_RECORD,
  FETCH_LOCATION_DISCIPLINARY,
  POST_DISCIPLINARY_ACTION,
  SAVE_DISCIPLINARY,
  UPLOAD_DISCIPLINARY_ATTACHMENT,
} from "./constants";

function errorMessage(error, fallback) {
  const fromBody = error?.response?.data?.message;
  if (typeof fromBody === "string" && fromBody.trim()) return fromBody;
  if (typeof error?.error === "string" && error.error.trim())
    return error.error;
  if (
    typeof error?.message === "string" &&
    error.message.trim() &&
    !error.message.startsWith("Request failed")
  ) {
    return error.message;
  }
  return fallback;
}

function notifyError(error, fallback) {
  toast.error(errorMessage(error, fallback), {
    position: "top-center",
    autoClose: 3500,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function unwrapRecord(payload) {
  if (payload?.uuid) return payload;
  if (payload?.data?.uuid) return payload.data;
  return payload?.data ?? null;
}

export function* fetchDisciplinarySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/employee-disciplinary/employee/${payload.employeeUuid}`,
    );
    yield put(fetchDisciplinarySuccess(unwrapList(data)));
  } catch (error) {
    notifyError(error, "Could not load disciplinary records.");
    yield put(fetchDisciplinaryFailure());
  }
}

export function* fetchLocationDisciplinarySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const params = {
    location_id: payload.locationId,
    page: payload.page || 1,
  };
  if (payload.status) params.status = payload.status;
  if (payload.employeeUuid) params.employee_uuid = payload.employeeUuid;

  try {
    const { data } = yield call(
      api(token).get,
      "/api/employee-disciplinary/location",
      { params },
    );
    yield put(
      fetchLocationDisciplinarySuccess({
        data: Array.isArray(data?.data) ? data.data : [],
        current_page: data?.current_page ?? 1,
        last_page: data?.last_page ?? 1,
        total: data?.total ?? 0,
      }),
    );
  } catch (error) {
    notifyError(error, "Could not load disciplinary cases.");
    yield put(fetchLocationDisciplinaryFailure());
  }
}

export function* fetchDisciplinaryRecordSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/employee-disciplinary/${payload.uuid}`,
    );
    yield put(fetchDisciplinaryRecordSuccess(unwrapRecord(data)));
  } catch (error) {
    notifyError(error, "Could not open the record.");
    yield put(fetchDisciplinaryRecordFailure());
  }
}

export function* saveDisciplinarySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      "/api/employee-disciplinary",
      payload,
    );
    const saved = unwrapRecord(data);
    yield put(saveDisciplinarySuccess(saved));
    yield put(fetchDisciplinary(payload.employee_uuid));
    if (typeof onSuccess === "function") onSuccess(saved);
  } catch (error) {
    notifyError(error, "Save failed.");
    yield put(saveDisciplinaryFailure());
  }
}

export function* postDisciplinaryActionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/employee-disciplinary/${payload.uuid}${payload.path}`,
      payload.body,
    );
    yield put(postDisciplinaryActionSuccess(unwrapRecord(data)));
    if (payload.employeeUuid) {
      yield put(fetchDisciplinary(payload.employeeUuid));
    }
    if (typeof onSuccess === "function") onSuccess(data);
  } catch (error) {
    notifyError(error, "Update failed.");
    yield put(postDisciplinaryActionFailure());
  }
}

export function* deleteDisciplinarySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    yield call(
      api(token).post,
      `/api/employee-disciplinary/${payload.uuid}/delete`,
      {},
    );
    yield put(deleteDisciplinarySuccess());
    if (payload.employeeUuid) {
      yield put(fetchDisciplinary(payload.employeeUuid));
    }
    if (typeof onSuccess === "function") onSuccess();
  } catch (error) {
    notifyError(error, "Could not delete the draft.");
    yield put(deleteDisciplinaryFailure());
  }
}

export function* uploadDisciplinaryAttachmentSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("document_type", payload.document_type);
  if (payload.is_sensitive !== undefined) {
    formData.append("is_sensitive", payload.is_sensitive ? 1 : 0);
  }

  try {
    const { data } = yield call(
      api(token).post,
      `/api/employee-disciplinary/${payload.uuid}/attachment`,
      formData,
    );
    yield put(uploadDisciplinaryAttachmentSuccess(data));
    yield put(fetchDisciplinaryRecord(payload.uuid));
    if (payload.employeeUuid) {
      yield put(fetchDisciplinary(payload.employeeUuid));
    }
    if (typeof onSuccess === "function") onSuccess(data);
  } catch (error) {
    notifyError(error, "Could not upload the document.");
    yield put(uploadDisciplinaryAttachmentFailure());
  }
}

export default function* DisciplinarySaga() {
  yield all([
    takeLatest(FETCH_DISCIPLINARY, fetchDisciplinarySaga),
    takeLatest(FETCH_LOCATION_DISCIPLINARY, fetchLocationDisciplinarySaga),
    takeLatest(FETCH_DISCIPLINARY_RECORD, fetchDisciplinaryRecordSaga),
    takeLatest(SAVE_DISCIPLINARY, saveDisciplinarySaga),
    takeLatest(POST_DISCIPLINARY_ACTION, postDisciplinaryActionSaga),
    takeLatest(DELETE_DISCIPLINARY, deleteDisciplinarySaga),
    takeLatest(
      UPLOAD_DISCIPLINARY_ATTACHMENT,
      uploadDisciplinaryAttachmentSaga,
    ),
  ]);
}
